// ═══════════════════════════════════════════════════════════════════════
// Module IPM — socle commun sécurisé (3 octobre 2026).
// Chaque onglet n'apparaît qu'avec l'habilitation IPM correspondante, attribuée explicitement
// (aucun accès par le seul rôle). La dette du salarié est la somme de ses restes à charge ; elle se
// lit dans un compte de mouvements. Le domaine financier n'affiche aucune donnée médicale.
// Socle visuel SOGAS RH : icônes lucide uniquement, aucun emoji. La refonte complète du module
// viendra après les véritables règles de l'IPM (règlement intérieur).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Wallet, CheckCheck, Stethoscope, Building2, Receipt, BarChart3, SlidersHorizontal, KeyRound, X, Plus,
  Lock, Undo2, FileText, ShieldAlert, RotateCcw, Send, Archive, Check, Ban, History, ScrollText, Users, Unlock,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  EnTetePage, Bouton, Carte, Badge, Etat, BandeauInfo, Onglets, EtatVide, Squelette, ChampRecherche,
  Pagination, Champ, Grille, formaterDate, NON_RENSEIGNE,
} from '../design/composants';
import {
  useApi, Fenetre, FenetreSaisie, ChampForm, PiedFenetre, fmtF, messageErreur, nomSalarie,
} from '../components/ipm/IpmCommun';

const moisCourant = () => new Date().toISOString().slice(0, 7);
const MSG_PROPRE_DOSSIER = 'Votre propre dossier IPM';

/** Recherche d'un salarié dans le périmètre de l'IPM (identité minimale). */
function RechercheSalarie({ api, valeur, onChoix, libelleChoix = 'Choisir', libelle = 'Rechercher un salarié' }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => { api.get('/salaries', { search: q }).then(setRes).catch(() => setRes([])); }, 250);
    return () => clearTimeout(t);
  }, [q, api]);
  if (valeur) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Badge ton="info">{nomSalarie(valeur)} · {valeur.matricule}</Badge>
        <Bouton petit variante="discret" Icone={RotateCcw} onClick={() => onChoix(null)}>Changer</Bouton>
      </div>
    );
  }
  return (
    <div>
      <ChampRecherche valeur={q} onChange={setQ} placeholder="Nom, prénom ou matricule" libelle={libelle} />
      {res.length > 0 && (
        <ul className="rh-liste" style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto' }}>
          {res.map((s) => (
            <li key={s.id}>
              <span style={{ flex: 1 }}>{nomSalarie(s)} <span className="rh-cellule-secondaire">· {s.matricule}{s.site_nom ? ` · ${s.site_nom}` : ''}{s.statut !== 'Actif' ? ` · ${s.statut}` : ''}</span></span>
              <Bouton petit onClick={() => { onChoix(s); setQ(''); }}>{libelleChoix}</Bouton>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const TON_RETENUE = { PROPOSEE: 'info', ARRETEE: 'alerte', RETOURNEE: 'succes', ANNULEE: 'neutre' };
const LIBELLE_RETENUE = { PROPOSEE: 'Proposée', ARRETEE: 'Arrêtée, en attente de la paie', RETOURNEE: 'Retour de la paie reçu', ANNULEE: 'Annulée' };
const TON_MVT = { VALIDE: 'succes', A_VALIDER: 'alerte', REJETE: 'neutre' };
const LIBELLE_MVT = { VALIDE: 'Validé', A_VALIDER: 'À valider', REJETE: 'Rejeté' };

// ═══ 1. DETTES ET RETENUES (domaine financier) ═════════════════════════════
function DetteSalarie({ api, employeId, droits, moi, onFermer, onChange }) {
  const propre = employeId === moi;
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [action, setAction] = useState(null);
  const charger = useCallback(() => { api.get(`/dettes/${employeId}`).then(setD).catch((e) => setErreur(messageErreur(e))); }, [api, employeId]);
  useEffect(() => { charger(); }, [charger]);
  const fait = () => { setAction(null); charger(); onChange && onChange(); };
  return (
    <Fenetre titre={d ? `Dette IPM — ${nomSalarie(d.employe)}` : 'Dette IPM'} onFermer={onFermer} large>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!d && !erreur && <Squelette hauteur={120} />}
      {d && (
        <>
          <BandeauInfo ton="info">La dette est la somme des restes à charge du salarié (jamais la part payée par l'IPM), diminuée des sommes récupérées. Elle se calcule à partir des mouvements validés.</BandeauInfo>
          <Grille>
            <Champ libelle="Dette restante" valeur={fmtF(d.solde)} />
            <Champ libelle="Restes à charge cumulés" valeur={fmtF(d.total_restes_a_charge)} />
            <Champ libelle="Déjà récupéré" valeur={fmtF(d.total_recupere)} />
            <Champ libelle="Annulations et régularisations" valeur={fmtF(d.total_regularisations - d.total_annulations)} />
            {d.trop_percu > 0 && <Champ libelle="Trop-perçu à restituer" valeur={fmtF(d.trop_percu)} />}
          </Grille>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
            <Bouton variante="primaire" Icone={Wallet} onClick={() => navigate(`/ipm/comptes/${employeId}`)}>Ouvrir le compte et le relevé</Bouton>
          </div>
          {propre && <BandeauInfo ton="alerte">{MSG_PROPRE_DOSSIER} : consultation seulement. Une autre personne habilitée doit le traiter.</BandeauInfo>}
          {droits.ipm_gestion && !propre && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
              <Bouton Icone={Plus} onClick={() => setAction('regularisation')}>Proposer une régularisation</Bouton>
              <Bouton Icone={Wallet} onClick={() => setAction('paiement')}>Enregistrer un paiement direct</Bouton>
            </div>
          )}
          <h3 style={{ fontSize: 14, margin: '16px 0 8px' }}>Mouvements</h3>
          {d.mouvements.length === 0 ? <EtatVide titre="Aucun mouvement" texte="Aucune prise en charge n'a encore créé de reste à charge." /> : (
            <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
              <table className="rh-tableau">
                <thead><tr><th>N°</th><th>Date</th><th>Nature</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th>Motif / référence</th><th>Saisi par</th><th>Validé par</th><th style={{ textAlign: 'right' }}>Dette après</th></tr></thead>
                <tbody>
                  {d.mouvements.map((m) => (
                    <tr key={m.id} className="rh-ligne-inactive">
                      <td>{m.numero}</td>
                      <td>{formaterDate(m.date_operation)}</td>
                      <td>{m.libelle_nature}{m.periode_retenue ? <div className="rh-cellule-secondaire">Paie {m.periode_retenue}</div> : null}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{m.sens > 0 ? '+' : '−'} {fmtF(m.montant)}</td>
                      <td><Etat ton={TON_MVT[m.statut]}>{LIBELLE_MVT[m.statut]}</Etat></td>
                      <td style={{ maxWidth: 240 }}>{m.motif || m.reference || (m.facture_ref ? `Prise en charge ${m.facture_ref}` : '')}{m.motif_rejet ? <div className="rh-cellule-secondaire">Rejet : {m.motif_rejet}</div> : null}</td>
                      <td>{m.cree_par_nom}</td>
                      <td>{m.origine === 'AUTOMATIQUE' ? 'Automatique' : (m.valide_par_nom || '—')}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{m.solde_apres === null ? '—' : fmtF(m.solde_apres)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h3 style={{ fontSize: 14, margin: '16px 0 8px' }}>Retenues mensuelles</h3>
          {d.retenues.length === 0 ? <p className="rh-cellule-secondaire">Aucune retenue proposée.</p> : (
            <ul className="rh-liste">
              {d.retenues.map((r) => (
                <li key={r.id}>
                  <span style={{ flex: 1 }}>{r.periode} · proposée {fmtF(r.montant_propose)} · applicable {fmtF(r.montant_applicable)} · retenue {fmtF(r.montant_retenu)}{r.motif_ecart ? ` · écart : ${r.motif_ecart}` : ''}</span>
                  <Etat ton={TON_RETENUE[r.statut]}>{LIBELLE_RETENUE[r.statut]}</Etat>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {action === 'regularisation' && (
        <FenetreSaisie titre="Proposer une régularisation" onFermer={() => setAction(null)} onFait={fait}
          avertissement="Elle ne prendra effet qu'après validation par une autre personne habilitée. Aucun mouvement existant n'est modifié."
          initial={{ sens: '1' }}
          champs={[
            { cle: 'sens', libelle: 'Effet sur la dette', requis: true, options: [{ valeur: '1', libelle: 'Augmente la dette' }, { valeur: '-1', libelle: 'Diminue la dette' }] },
            { cle: 'montant', libelle: 'Montant (FCFA)', requis: true, type: 'number', min: 1 },
            { cle: 'motif', libelle: 'Motif', requis: true, texteLong: true, aide: 'Visible par les contrôles RH, paie et finance : n\'y écrivez aucune information médicale.' },
            { cle: 'reference', libelle: 'Référence (pièce, courrier)' },
          ]}
          valider={(v) => api.post(`/dettes/${employeId}/regularisations`, { ...v, sens: Number(v.sens) })} />
      )}
      {action === 'paiement' && (
        <FenetreSaisie titre="Paiement direct du salarié" onFermer={() => setAction(null)} onFait={fait}
          avertissement="Paiement par chèque ou virement uniquement. Il ne prendra effet qu'après validation par une autre personne habilitée."
          champs={[
            { cle: 'montant', libelle: 'Montant (FCFA)', requis: true, type: 'number', min: 1 },
            { cle: 'date_operation', libelle: 'Date du paiement', type: 'date' },
            { cle: 'reference', libelle: 'Référence du chèque ou du virement', requis: true },
            { cle: 'motif', libelle: 'Motif', requis: true, texteLong: true, aide: 'Visible par les contrôles RH, paie et finance : n\'y écrivez aucune information médicale.' },
          ]}
          valider={(v) => api.post(`/dettes/${employeId}/paiements-directs`, v)} />
      )}
    </Fenetre>
  );
}

function SimulationRetenue({ api, employeId, periode, montant }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!(Number(montant) > 0)) { setS(null); return undefined; }
    const t = setTimeout(() => { api.get('/retenues/simulation', { employe_id: employeId, periode, montant }).then(setS).catch(() => setS(null)); }, 300);
    return () => clearTimeout(t);
  }, [api, employeId, periode, montant]);
  if (!s) return null;
  return (
    <BandeauInfo
      ton={s.montant_applicable !== null && s.montant_applicable < Number(montant) ? 'alerte' : 'info'}
      titre={s.montant_applicable === null ? 'Montant applicable : non communiqué (limite liée à la paie)' : `Montant applicable : ${fmtF(s.montant_applicable)}`}>
      {s.limites.map((l) => <div key={l.code}>{l.libelle} : {fmtF(l.montant)}</div>)}
      {s.autres_retenues?.nombre > 0 && <div>Autres retenues connues : {fmtF(s.autres_retenues.total)} ({s.autres_retenues.nombre})</div>}
      {s.alertes.map((a) => <div key={a.code}>{a.message}</div>)}
    </BandeauInfo>
  );
}

function ControleRetenues({ api, droits, moi }) {
  const [periode, setPeriode] = useState(moisCourant());
  const [recherche, setRecherche] = useState('');
  const [tous, setTous] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [action, setAction] = useState(null);
  const [message, setMessage] = useState(null);
  const charger = useCallback(() => {
    setErreur(null);
    api.get('/controle', { periode, search: recherche || undefined, tous: tous ? 'true' : undefined, page, limit: 25 })
      .then(setData).catch((e) => setErreur(messageErreur(e)));
  }, [api, periode, recherche, tous, page]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  const fait = (m) => { setAction(null); if (m) setMessage(m); charger(); };
  // Arrêt en deux temps : OUVERTE → ARRET_DEMANDE (gestion) → ARRETEE (supervision, autre personne).
  const statutPeriode = data?.statut_periode || 'OUVERTE';
  const ouverte = statutPeriode === 'OUVERTE';
  const arretDemande = statutPeriode === 'ARRET_DEMANDE';
  const demande = data?.demande_arret || null;
  const demandeParMoi = !!(demande && demande.par === moi);
  const MSG_FIGE = 'Arrêt demandé : annulez la demande pour modifier les retenues.';
  return (
    <>
      <div className="rh-outils" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Période
          <input type="month" className="rh-champ-saisie" style={{ width: 'auto' }} value={periode} onChange={(e) => { setPeriode(e.target.value); setPage(1); }} />
        </label>
        <ChampRecherche valeur={recherche} onChange={(v) => { setRecherche(v); setPage(1); }} placeholder="Nom, prénom ou matricule" />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={tous} onChange={(e) => { setTous(e.target.checked); setPage(1); }} /> Inclure les dettes soldées
        </label>
        <span style={{ flex: 1 }} />
        {data && (
          <Etat ton={ouverte ? 'info' : 'alerte'}>
            {ouverte ? 'Période ouverte' : arretDemande ? 'Arrêt demandé' : `Période arrêtée le ${formaterDate(data.arretee_le)}`}
          </Etat>
        )}
        {droits.ipm_gestion && data && ouverte && (
          <Bouton Icone={Lock} onClick={() => setAction({ type: 'demander-arret' })}>Demander l'arrêt</Bouton>
        )}
      </div>
      {data && arretDemande && (
        <BandeauInfo ton="alerte" titre={`Arrêt demandé par ${demande?.par_nom || 'un membre de l\'IPM'}${demande?.le ? ` le ${formaterDate(demande.le)}` : ''} — en attente de confirmation par un Gestionnaire IPM`}>
          Les retenues sont figées jusqu'à la confirmation ou l'annulation de la demande.
          {demandeParMoi && droits.ipm_supervision && <div>Vous avez demandé cet arrêt : un autre Gestionnaire IPM doit le confirmer.</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {droits.ipm_supervision && !demandeParMoi && <Bouton petit variante="primaire" Icone={Lock} onClick={() => setAction({ type: 'confirmer-arret' })}>Confirmer l'arrêt</Bouton>}
            {droits.ipm_gestion && (demandeParMoi || droits.ipm_supervision) && <Bouton petit variante="discret" Icone={Unlock} onClick={() => setAction({ type: 'annuler-demande' })}>Annuler la demande</Bouton>}
          </div>
        </BandeauInfo>
      )}
      <BandeauInfo ton="info">Aucune donnée médicale ici : ni soin, ni prestataire, ni ordonnance. Le montant applicable est le plus petit de la retenue proposée, de la dette et des limites paramétrées ; le reliquat reste dans la dette.</BandeauInfo>
      {message && <BandeauInfo ton="succes" action={<Bouton petit variante="discret" Icone={X} onClick={() => setMessage(null)} aria-label="Fermer le message" />}>{message}</BandeauInfo>}
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!data && !erreur && <Squelette hauteur={160} />}
      {data && data.lignes.length === 0 && <EtatVide titre="Aucune dette IPM" texte="Aucun salarié n'a de dette ou de retenue pour cette période." />}
      {data && data.lignes.length > 0 && (
        <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
          <table className="rh-tableau">
            <thead><tr>
              <th>Salarié</th><th style={{ textAlign: 'right' }}>Dette</th><th style={{ textAlign: 'right' }}>Nouveaux restes à charge</th>
              <th style={{ textAlign: 'right' }}>Déjà récupéré</th><th style={{ textAlign: 'right' }}>Retenue proposée</th>
              <th style={{ textAlign: 'right' }}>Autres retenues</th><th style={{ textAlign: 'right' }}>Applicable</th>
              <th style={{ textAlign: 'right' }}>Reste après</th><th>Contrôle</th><th aria-label="Actions" />
            </tr></thead>
            <tbody>
              {data.lignes.map((l) => {
                const propre = l.employe_id === moi;
                return (
                  <tr key={l.employe_id} className="rh-ligne-inactive">
                    <td><div className="rh-cellule-principale">{nomSalarie(l)}</div><div className="rh-cellule-secondaire">{l.matricule}</div></td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(l.dette)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(l.nouveaux_restes_a_charge)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(l.total_recupere)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{l.retenue ? fmtF(l.retenue.montant_propose) : '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{l.autres_retenues?.nombre ? fmtF(l.autres_retenues.total) : '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{l.retenue ? fmtF(l.retenue.montant_applicable) : '—'}{l.retenue?.montant_retenu != null ? <div className="rh-cellule-secondaire">retenu {fmtF(l.retenue.montant_retenu)}</div> : null}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(l.reste_apres_retenue)}</td>
                    <td style={{ minWidth: 160 }}>
                      {l.retenue && <Etat ton={TON_RETENUE[l.retenue.statut]}>{LIBELLE_RETENUE[l.retenue.statut]}</Etat>}
                      {l.alertes.map((a) => <div key={a.code} className="rh-cellule-secondaire" style={{ color: 'var(--sg-alerte, #9A6700)' }}>{a.message}</div>)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Bouton petit variante="discret" Icone={History} onClick={() => setAction({ type: 'detail', ligne: l })}>Détail</Bouton>
                      {propre && (droits.ipm_gestion || droits.ipm_controle_paie) ? (
                        <span className="rh-cellule-secondaire" style={{ marginLeft: 6 }}>{MSG_PROPRE_DOSSIER}</span>
                      ) : (
                        <>
                          {droits.ipm_gestion && ouverte && !l.retenue && l.dette > 0 && <Bouton petit Icone={Plus} onClick={() => setAction({ type: 'proposer', ligne: l })}>Proposer</Bouton>}
                          {droits.ipm_gestion && arretDemande && !l.retenue && l.dette > 0 && <Bouton petit Icone={Plus} disabled title={MSG_FIGE}>Proposer</Bouton>}
                          {droits.ipm_gestion && l.retenue?.statut === 'PROPOSEE' && (
                            <>
                              <Bouton petit disabled={!ouverte} title={ouverte ? undefined : MSG_FIGE} onClick={() => setAction({ type: 'modifier', ligne: l })}>Modifier</Bouton>
                              <Bouton petit variante="discret" Icone={Ban} disabled={!ouverte} title={ouverte ? undefined : MSG_FIGE} onClick={() => setAction({ type: 'annuler', ligne: l })}>Annuler</Bouton>
                            </>
                          )}
                          {droits.ipm_controle_paie && l.retenue?.statut === 'ARRETEE' && <Bouton petit Icone={Undo2} onClick={() => setAction({ type: 'retour', ligne: l })}>Retour de la paie</Bouton>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={data.page} pages={Math.max(1, Math.ceil(data.total / data.limite))} total={data.total} taille={data.limite} onPage={setPage} />}

      {action?.type === 'detail' && <DetteSalarie api={api} employeId={action.ligne.employe_id} droits={droits} moi={moi} onFermer={() => setAction(null)} onChange={charger} />}
      {action?.type === 'demander-arret' && (
        <FenetreSaisie titre={`Demander l'arrêt de la période ${periode}`} onFermer={() => setAction(null)} onFait={(r) => fait((r && r.message) || 'Arrêt demandé.')}
          avertissement="Les retenues sont figées dès la demande. L'arrêt ne devient définitif qu'après confirmation par un Gestionnaire IPM, autre que vous ; la demande peut être annulée d'ici là."
          champs={[]} libelleBouton="Demander l'arrêt" valider={() => api.post(`/periodes/${periode}/demande-arret`)} />
      )}
      {action?.type === 'confirmer-arret' && (
        <FenetreSaisie titre={`Confirmer l'arrêt de la période ${periode}`} onFermer={() => setAction(null)} onFait={() => fait(`Période ${periode} arrêtée : les montants applicables sont figés et transmis à la paie.`)}
          tonAvertissement="alerte"
          avertissement="Définitif : le montant applicable de chaque retenue est figé et transmis à la paie. La période ne se modifie plus ; aucun paramètre ne pourra plus prendre effet sur elle."
          champs={[]} libelleBouton="Confirmer l'arrêt" valider={() => api.post(`/periodes/${periode}/arreter`)} />
      )}
      {action?.type === 'annuler-demande' && (
        <FenetreSaisie titre={`Annuler la demande d'arrêt — ${periode}`} onFermer={() => setAction(null)} onFait={(r) => fait((r && r.message) || 'Demande d\'arrêt annulée.')}
          avertissement="La période redevient ouverte : les retenues peuvent de nouveau être modifiées."
          champs={[{ cle: 'motif', libelle: 'Motif', requis: true, texteLong: true }]}
          libelleBouton="Annuler la demande" valider={(v) => api.post(`/periodes/${periode}/annuler-demande`, v)} />
      )}
      {action?.type === 'proposer' && (
        <FenetreSaisie titre={`Proposer une retenue — ${nomSalarie(action.ligne)}`} onFermer={() => setAction(null)} onFait={() => fait('Retenue proposée.')}
          avertissement={`Dette actuelle : ${fmtF(action.ligne.dette)}. Le montant est libre ; seul le montant applicable sera retenu.`}
          champs={[{ cle: 'montant', libelle: 'Retenue proposée pour la période (FCFA)', requis: true, type: 'number', min: 1 }, { cle: 'motif', libelle: 'Commentaire', texteLong: true }]}
          enfant={(v) => <SimulationRetenue api={api} employeId={action.ligne.employe_id} periode={periode} montant={v.montant} />}
          valider={(v) => api.post('/retenues', { employe_id: action.ligne.employe_id, periode, montant: v.montant, motif: v.motif })} />
      )}
      {action?.type === 'modifier' && (
        <FenetreSaisie titre={`Modifier la retenue — ${nomSalarie(action.ligne)}`} onFermer={() => setAction(null)} onFait={() => fait('Retenue modifiée.')}
          initial={{ montant: action.ligne.retenue.montant_propose }}
          champs={[{ cle: 'montant', libelle: 'Nouvelle retenue proposée (FCFA)', requis: true, type: 'number', min: 1 }, { cle: 'motif', libelle: 'Motif de la modification', requis: true, texteLong: true }]}
          enfant={(v) => <SimulationRetenue api={api} employeId={action.ligne.employe_id} periode={periode} montant={v.montant} />}
          valider={(v) => api.patch(`/retenues/${action.ligne.retenue.id}`, v)} />
      )}
      {action?.type === 'annuler' && (
        <FenetreSaisie titre={`Annuler la retenue — ${nomSalarie(action.ligne)}`} onFermer={() => setAction(null)} onFait={() => fait('Retenue annulée.')}
          champs={[{ cle: 'motif', libelle: 'Motif de l\'annulation', requis: true, texteLong: true }]}
          valider={(v) => api.post(`/retenues/${action.ligne.retenue.id}/annuler`, v)} />
      )}
      {action?.type === 'retour' && (
        <FenetreSaisie titre={`Retour de la paie — ${nomSalarie(action.ligne)}`} onFermer={() => setAction(null)} onFait={() => fait('Retour de la paie enregistré.')}
          avertissement={`Montant applicable arrêté : ${fmtF(action.ligne.retenue.montant_applicable)}. Saisir le montant réellement retenu sur le bulletin ; le reliquat reste dans la dette.`}
          initial={{ montant_retenu: action.ligne.retenue.montant_applicable }}
          champs={[
            { cle: 'montant_retenu', libelle: 'Montant réellement retenu (FCFA)', requis: true, type: 'number', min: 0 },
            { cle: 'reference_bulletin', libelle: 'Référence du bulletin' },
            { cle: 'motif_ecart', libelle: 'Motif de l\'écart (obligatoire si moins que l\'applicable)', texteLong: true },
          ]}
          valider={(v) => api.post(`/retenues/${action.ligne.retenue.id}/retour-paie`, v)} />
      )}
    </>
  );
}

// ═══ 2. À VALIDER (double regard) ═══════════════════════════════════════════
function AValider({ api, droits, moi }) {
  const superviseur = !!droits.ipm_supervision;
  const [mvts, setMvts] = useState(null);
  const [params, setParams] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [rejet, setRejet] = useState(null);
  const charger = useCallback(() => {
    api.get('/mouvements/a-valider').then(setMvts).catch((e) => setErreur(messageErreur(e)));
    api.get('/parametres-versions', { statut: 'A_VALIDER' }).then(setParams).catch(() => setParams([]));
  }, [api]);
  useEffect(() => { charger(); }, [charger]);
  const agir = async (fn) => { setErreur(null); try { await fn(); charger(); } catch (e) { setErreur(messageErreur(e)); } };
  return (
    <>
      <BandeauInfo ton="info">Double regard : la validation relève du Gestionnaire IPM, et l'auteur d'une correction ou d'un paramètre ne peut jamais la valider lui-même.{superviseur ? '' : ' Vos propositions restent en attente d\'un Gestionnaire IPM.'}</BandeauInfo>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Carte titre="Mouvements de dette à valider" Icone={Wallet}>
        {!mvts ? <Squelette hauteur={60} /> : mvts.length === 0 ? <EtatVide titre="Rien à valider" /> : (
          <ul className="rh-liste">
            {mvts.map((m) => (
              <li key={m.id}>
                <span style={{ flex: 1 }}>
                  <strong>{m.libelle_nature}</strong> · {m.sens > 0 ? '+' : '−'} {fmtF(m.montant)} · {nomSalarie(m)} ({m.matricule})
                  <div className="rh-cellule-secondaire">{m.motif} — proposé par {m.cree_par_nom} le {formaterDate(m.cree_le)}</div>
                </span>
                {!superviseur ? <Etat ton="alerte">En attente du Gestionnaire IPM</Etat>
                  : m.cree_par === moi ? <Etat ton="neutre">Vous en êtes l'auteur</Etat>
                  : m.employe_id === moi ? <Etat ton="neutre">Votre propre dossier</Etat> : (
                  <>
                    <Bouton petit variante="primaire" Icone={Check} onClick={() => agir(() => api.post(`/mouvements/${m.id}/valider`))}>Valider</Bouton>
                    <Bouton petit variante="discret" Icone={Ban} onClick={() => setRejet(m)}>Rejeter</Bouton>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Carte>
      <Carte titre="Paramètres à valider" Icone={SlidersHorizontal}>
        {params.length === 0 ? <EtatVide titre="Rien à valider" /> : (
          <ul className="rh-liste">
            {params.map((p) => (
              <li key={p.id}>
                <span style={{ flex: 1 }}><strong>{p.libelle}</strong>{p.contexte ? ` (${p.contexte})` : ''} · v{p.version} · {p.valeur} {p.unite === 'POURCENT' ? '%' : p.unite.toLowerCase()} · dès le {formaterDate(p.date_debut)}
                  <div className="rh-cellule-secondaire">Source : {p.source_reference} — saisi par {p.cree_par_nom}</div></span>
                {!superviseur ? <Etat ton="alerte">En attente du Gestionnaire IPM</Etat>
                  : p.cree_par === moi ? <Etat ton="neutre">Vous en êtes l'auteur</Etat>
                  : <Bouton petit variante="primaire" Icone={Check} onClick={() => agir(() => api.post(`/parametres-versions/${p.id}/valider`))}>Valider</Bouton>}
              </li>
            ))}
          </ul>
        )}
      </Carte>
      {rejet && (
        <FenetreSaisie titre="Rejeter le mouvement" onFermer={() => setRejet(null)} onFait={() => { setRejet(null); charger(); }}
          champs={[{ cle: 'motif', libelle: 'Motif du rejet', requis: true, texteLong: true }]}
          valider={(v) => api.post(`/mouvements/${rejet.id}/rejeter`, v)} />
      )}
    </>
  );
}

// ═══ 3. PRESTATIONS (domaine médical) ═══════════════════════════════════════
function NouvellePrestation({ api, moi, onEnregistree }) {
  const [salarie, setSalarie] = useState(null);
  const [benefs, setBenefs] = useState(null);
  const [benef, setBenef] = useState(null);
  const [soins, setSoins] = useState([]);
  const [etabs, setEtabs] = useState([]);
  const [f, setF] = useState({ type_soin_id: '', etablissement_id: '', montant_facture: '', date_soin: new Date().toISOString().slice(0, 10), reference_facture: '', ordonnance_agreee: false });
  const [erreur, setErreur] = useState(null);
  const [resultat, setResultat] = useState(null);
  const envoi = useRef(false);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    api.get('/types-soins').then((r) => setSoins(r.filter((s) => s.actif))).catch(() => {});
    api.get('/etablissements').then(setEtabs).catch(() => {});
  }, [api]);
  useEffect(() => {
    setBenef(null); setBenefs(null); setResultat(null);
    if (salarie) api.get(`/employe/${salarie.id}/beneficiaires`).then((r) => setBenefs(r)).catch((e) => setErreur(messageErreur(e)));
  }, [salarie, api]);
  const soin = soins.find((s) => s.id === f.type_soin_id);
  const etab = etabs.find((e) => e.id === f.etablissement_id);
  const tauxBase = soin && etab ? (etab.agree ? soin.taux_actif : soin.taux_non_reconnu_actif) : null;
  const tauxManquant = soin && etab && tauxBase === null;
  const estimation = soin && etab && Number(f.montant_facture) > 0 && tauxBase !== null
    ? (() => { const ipm = Math.round(Number(f.montant_facture) * tauxBase / 100); return { t: tauxBase, ipm, reste: Number(f.montant_facture) - ipm }; })() : null;
  const enregistrer = async () => {
    if (envoi.current) return;
    envoi.current = true; setEnCours(true); setErreur(null);
    try {
      const r = await api.post('/factures', { employe_id: salarie.id, beneficiaire_type: benef.type, beneficiaire_id: benef.type === 'employe' ? null : benef.id, ...f });
      setResultat(r); onEnregistree && onEnregistree();
      setF({ ...f, montant_facture: '', reference_facture: '', ordonnance_agreee: false });
    } catch (e) { setErreur(messageErreur(e)); } finally { envoi.current = false; setEnCours(false); }
  };
  const propre = !!salarie && salarie.id === moi;
  const pret = salarie && !propre && benef && f.type_soin_id && f.etablissement_id && Number(f.montant_facture) > 0 && f.date_soin;
  return (
    <Carte titre="Nouvelle prestation" Icone={Plus}>
      <ChampForm libelle="Salarié" requis><RechercheSalarie api={api} valeur={salarie} onChoix={setSalarie} /></ChampForm>
      {propre && <BandeauInfo ton="alerte">{MSG_PROPRE_DOSSIER} : une autre personne habilitée doit enregistrer cette prestation.</BandeauInfo>}
      {salarie && benefs && (
        <ChampForm libelle="Bénéficiaire" requis aide={benefs.age_max_parametre ? null : 'Âge maximal des enfants non paramétré : une prise en charge pour un enfant sera refusée tant qu\'il ne l\'est pas.'}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {benefs.beneficiaires.map((b) => (
              <Bouton key={b.id} petit variante={benef?.id === b.id ? 'primaire' : 'secondaire'} disabled={!b.eligible} title={b.motif_ineligible || undefined} onClick={() => setBenef(b)}>
                {b.type === 'employe' ? 'Le salarié' : `${b.prenom} (${b.type === 'conjoint' ? 'épouse' : 'enfant'})`}
              </Bouton>
            ))}
          </div>
          {benefs.beneficiaires.filter((b) => !b.eligible || b.justificatif?.en_delai).map((b) => (
            <div key={b.id} className="rh-cellule-secondaire">{b.prenom} : {b.motif_ineligible || b.justificatif?.motif}</div>
          ))}
        </ChampForm>
      )}
      <div className="rh-form-grille" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 16px' }}>
        <ChampForm libelle="Type de soin" requis id="p-soin">
          <select id="p-soin" className="rh-champ-saisie" value={f.type_soin_id} onChange={(e) => setF({ ...f, type_soin_id: e.target.value })}>
            <option value="">Choisir</option>
            {soins.map((s) => <option key={s.id} value={s.id} disabled={s.taux_actif === null && s.taux_non_reconnu_actif === null}>{s.libelle}{s.taux_actif === null ? ' — taux non paramétré' : ` — ${s.taux_actif} %`}{s.taux_non_reconnu_actif === null ? '' : ` (non reconnu : ${s.taux_non_reconnu_actif} %)`}</option>)}
          </select>
        </ChampForm>
        <ChampForm libelle="Prestataire" requis id="p-etab">
          <select id="p-etab" className="rh-champ-saisie" value={f.etablissement_id} onChange={(e) => setF({ ...f, etablissement_id: e.target.value })}>
            <option value="">Choisir</option>
            {etabs.map((e) => <option key={e.id} value={e.id}>{e.nom}{e.ville ? ` (${e.ville})` : ''} — {e.agree ? 'reconnu' : 'non reconnu'}</option>)}
          </select>
        </ChampForm>
        <ChampForm libelle="Montant de la facture (FCFA)" requis id="p-mt"><input id="p-mt" className="rh-champ-saisie" type="number" min="1" value={f.montant_facture} onChange={(e) => setF({ ...f, montant_facture: e.target.value })} /></ChampForm>
        <ChampForm libelle="Date des soins" requis id="p-date"><input id="p-date" className="rh-champ-saisie" type="date" max={new Date().toISOString().slice(0, 10)} value={f.date_soin} onChange={(e) => setF({ ...f, date_soin: e.target.value })} /></ChampForm>
        <ChampForm libelle="Référence de la facture" id="p-ref"><input id="p-ref" className="rh-champ-saisie" value={f.reference_facture} onChange={(e) => setF({ ...f, reference_facture: e.target.value })} /></ChampForm>
      </div>
      {soin?.code === 'pharmacie' && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
          <input type="checkbox" checked={f.ordonnance_agreee} onChange={(e) => setF({ ...f, ordonnance_agreee: e.target.checked })} /> Ordonnance agréée présentée
        </label>
      )}
      {estimation && (
        <BandeauInfo ton={estimation.t === 0 ? 'alerte' : 'info'} titre={`Estimation : l'IPM paie ${fmtF(estimation.ipm)}, reste à charge du salarié ${fmtF(estimation.reste)}`}>
          {etab && !etab.agree ? `Prestataire non reconnu par l'IPM : taux paramétré de ${estimation.t} %. ` : ''}Le calcul définitif est fait par le serveur (règles d'âge, d'ordonnance et de délai). Le reste à charge s'ajoute à la dette IPM du salarié.
        </BandeauInfo>
      )}
      {tauxManquant && (
        <BandeauInfo ton="alerte">{etab.agree ? 'Aucun taux actif pour ce soin chez un prestataire reconnu.' : 'Aucun taux actif pour ce soin chez un prestataire non reconnu.'} La prise en charge sera refusée tant que le paramètre officiel n'est pas validé.</BandeauInfo>
      )}
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {resultat && (
        <BandeauInfo ton="succes" titre="Prestation enregistrée">
          Taux appliqué {resultat.taux_applique} % · payé par l'IPM {fmtF(resultat.part_ipm)} · reste à charge {fmtF(resultat.reste_a_charge)}{resultat.motif_taux_zero ? ` (${resultat.motif_taux_zero})` : ''} · dette du salarié après : {fmtF(resultat.dette_apres)}.
        </BandeauInfo>
      )}
      <PiedFenetre><Bouton variante="primaire" Icone={Check} onClick={enregistrer} disabled={!pret || enCours}>{enCours ? 'Enregistrement…' : 'Enregistrer la prestation'}</Bouton></PiedFenetre>
    </Carte>
  );
}

function HistoriquePrestations({ api, droits, moi, version }) {
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [data, setData] = useState(null);
  const [annulation, setAnnulation] = useState(null);
  const [message, setMessage] = useState(null);
  const charger = useCallback(() => { api.get('/factures', { page, limit: 25, search: recherche || undefined }).then(setData).catch(() => setData({ lignes: [], total: 0, page: 1, limite: 25 })); }, [api, page, recherche]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger, version]);
  const peutAnnuler = droits.ipm_gestion && droits.ipm_donnees_medicales;
  return (
    <Carte titre="Historique des prestations" Icone={FileText}>
      <div style={{ marginBottom: 10 }}><ChampRecherche valeur={recherche} onChange={(v) => { setRecherche(v); setPage(1); }} placeholder="Nom, prénom ou matricule" /></div>
      {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
      {!data ? <Squelette hauteur={80} /> : data.lignes.length === 0 ? <EtatVide titre="Aucune prestation" /> : (
        <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
          <table className="rh-tableau">
            <thead><tr><th>Date</th><th>Salarié</th><th>Bénéficiaire</th><th>Soin</th><th>Prestataire</th><th style={{ textAlign: 'right' }}>Facture</th><th style={{ textAlign: 'right' }}>Payé par l'IPM</th><th style={{ textAlign: 'right' }}>Reste à charge</th><th>État</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {data.lignes.map((f) => (
                <tr key={f.id} className="rh-ligne-inactive">
                  <td>{formaterDate(f.date_soin)}</td>
                  <td>{nomSalarie(f)}<div className="rh-cellule-secondaire">{f.matricule}</div></td>
                  <td>{f.beneficiaire_prenom || NON_RENSEIGNE}</td>
                  <td>{f.type_soin_libelle}{f.motif_taux_zero ? <div className="rh-cellule-secondaire">{f.motif_taux_zero}</div> : null}</td>
                  <td>{f.etablissement_nom || NON_RENSEIGNE}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(f.montant_facture)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(f.part_ipm)} <span className="rh-cellule-secondaire">({f.taux_applique} %)</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtF(f.part_employe)}</td>
                  <td>{f.statut === 'ANNULEE' ? <Etat ton="neutre">Annulée</Etat> : f.annulation_en_attente ? <Etat ton="alerte">Annulation à valider</Etat> : <Etat ton="succes">Enregistrée</Etat>}</td>
                  <td>{peutAnnuler && f.statut === 'ENREGISTREE' && !f.annulation_en_attente && f.employe_id !== moi && <Bouton petit variante="discret" Icone={Ban} onClick={() => setAnnulation(f)}>Annuler</Bouton>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={data.page} pages={Math.max(1, Math.ceil(data.total / data.limite))} total={data.total} taille={data.limite} onPage={setPage} />}
      {annulation && (
        <FenetreSaisie titre="Demander l'annulation de la prestation" onFermer={() => setAnnulation(null)}
          onFait={() => { setAnnulation(null); setMessage('Annulation demandée : elle prendra effet après validation par une autre personne habilitée.'); charger(); }}
          avertissement={`Le reste à charge (${fmtF(annulation.part_employe)}) sera retiré de la dette après validation. La prestation reste dans l'historique. Le motif reste dans le domaine médical : le compte de dette n'en montre qu'une mention neutre.`}
          champs={[{ cle: 'motif', libelle: 'Motif', requis: true, texteLong: true }]}
          valider={(v) => api.post(`/factures/${annulation.id}/annulation`, v)} />
      )}
    </Carte>
  );
}

function Prestations({ api, droits, moi, exceptionnel }) {
  const [version, setVersion] = useState(0);
  return (
    <>
      {exceptionnel && <BandeauInfo ton="alerte" titre="Accès exceptionnel en cours">Lecture seule, limitée dans le temps ; chaque consultation est tracée.</BandeauInfo>}
      {droits.ipm_gestion && droits.ipm_donnees_medicales && <NouvellePrestation api={api} moi={moi} onEnregistree={() => setVersion((v) => v + 1)} />}
      <HistoriquePrestations api={api} droits={droits} moi={moi} version={version} />
    </>
  );
}

// ═══ 4. PRESTATAIRES ════════════════════════════════════════════════════════
const TYPES_ETAB = [['hopital', 'Hôpital'], ['clinique', 'Clinique'], ['pharmacie', 'Pharmacie'], ['laboratoire', 'Laboratoire'], ['opticien', 'Opticien'], ['autre', 'Autre']];
function Prestataires({ api, droits }) {
  const [liste, setListe] = useState(null);
  const [action, setAction] = useState(null);
  const charger = useCallback(() => { api.get('/etablissements').then(setListe).catch(() => setListe([])); }, [api]);
  useEffect(() => { charger(); }, [charger]);
  const fait = () => { setAction(null); charger(); };
  return (
    <Carte titre="Prestataires" Icone={Building2} action={droits.ipm_gestion && <Bouton Icone={Plus} onClick={() => setAction({ type: 'ajout' })}>Ajouter</Bouton>}>
      <BandeauInfo ton="info">Le taux chez un prestataire non reconnu est un paramètre daté et sourcé (« Taux de prise en charge chez un prestataire non reconnu »). La reconnaissance se change par un acte distinct, motivé et tracé.</BandeauInfo>
      {!liste ? <Squelette hauteur={60} /> : liste.length === 0 ? <EtatVide titre="Aucun prestataire" texte="La liste réelle viendra des conventions de l'IPM." /> : (
        <ul className="rh-liste">
          {liste.map((e) => (
            <li key={e.id}>
              <span style={{ flex: 1 }}>{e.nom}<span className="rh-cellule-secondaire"> · {(TYPES_ETAB.find((t) => t[0] === e.type) || [])[1] || e.type}{e.ville ? ` · ${e.ville}` : ''}</span></span>
              <Etat ton={e.agree ? 'succes' : 'neutre'}>{e.agree ? 'Reconnu par l\'IPM' : 'Non reconnu'}</Etat>
              {droits.ipm_gestion && <Bouton petit variante="discret" onClick={() => setAction({ type: 'modif', e })}>Modifier</Bouton>}
              {droits.ipm_supervision && <Bouton petit onClick={() => setAction({ type: 'reco', e })}>{e.agree ? 'Retirer la reconnaissance' : 'Reconnaître'}</Bouton>}
            </li>
          ))}
        </ul>
      )}
      {action?.type === 'ajout' && (
        <FenetreSaisie titre="Ajouter un prestataire" onFermer={() => setAction(null)} onFait={fait} initial={{ type: 'clinique', agree: false }}
          avertissement={droits.ipm_supervision ? null : 'Le prestataire est créé « non reconnu » : la reconnaissance relève du Gestionnaire IPM.'}
          champs={[
            { cle: 'nom', libelle: 'Nom', requis: true }, { cle: 'type', libelle: 'Type', requis: true, options: TYPES_ETAB.map(([valeur, libelle]) => ({ valeur, libelle })) },
            { cle: 'ville', libelle: 'Ville' }, { cle: 'adresse', libelle: 'Adresse' }, { cle: 'telephone', libelle: 'Téléphone' },
            ...(droits.ipm_supervision ? [
              { cle: 'agree', libelle: 'Reconnu par l\'IPM', type: 'checkbox' },
              { cle: 'motif', libelle: 'Référence de la reconnaissance (obligatoire si reconnu)', aide: 'Convention, liste officielle de l\'IPM, courrier…' },
            ] : []),
          ]}
          valider={(v) => api.post('/etablissements', { ...v, agree: droits.ipm_supervision ? v.agree === true : false })} />
      )}
      {action?.type === 'modif' && (
        <FenetreSaisie titre={`Modifier — ${action.e.nom}`} onFermer={() => setAction(null)} onFait={fait} initial={{ nom: action.e.nom, type: action.e.type, ville: action.e.ville || '', adresse: action.e.adresse || '', telephone: action.e.telephone || '' }}
          avertissement="Les coordonnées seulement : la reconnaissance ne change pas ici."
          champs={[{ cle: 'nom', libelle: 'Nom', requis: true }, { cle: 'type', libelle: 'Type', requis: true, options: TYPES_ETAB.map(([valeur, libelle]) => ({ valeur, libelle })) }, { cle: 'ville', libelle: 'Ville' }, { cle: 'adresse', libelle: 'Adresse' }, { cle: 'telephone', libelle: 'Téléphone' }]}
          valider={(v) => api.patch(`/etablissements/${action.e.id}`, v)} />
      )}
      {action?.type === 'reco' && (
        <FenetreSaisie titre={action.e.agree ? `Retirer la reconnaissance — ${action.e.nom}` : `Reconnaître — ${action.e.nom}`} onFermer={() => setAction(null)} onFait={fait}
          champs={[{ cle: 'motif', libelle: 'Motif et référence', requis: true, texteLong: true }]}
          valider={(v) => api.post(`/etablissements/${action.e.id}/reconnaissance`, { agree: !action.e.agree, motif: v.motif })} />
      )}
    </Carte>
  );
}

// ═══ 5. COTISATIONS ═════════════════════════════════════════════════════════
function Cotisations({ api, droits }) {
  const [periode, setPeriode] = useState(moisCourant());
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [message, setMessage] = useState(null);
  const [action, setAction] = useState(null);
  const charger = useCallback(() => { api.get('/cotisations', { periode, page, limit: 25 }).then(setData).catch((e) => setMessage({ ton: 'danger', texte: messageErreur(e) })); }, [api, periode, page]);
  useEffect(() => { charger(); }, [charger]);
  const fait = (texte) => { setAction(null); setMessage({ ton: 'succes', texte }); charger(); };
  return (
    <Carte titre="Cotisations" Icone={Receipt}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Période
          <input type="month" className="rh-champ-saisie" style={{ width: 'auto' }} value={periode} onChange={(e) => { setPeriode(e.target.value); setPage(1); }} />
        </label>
        <span style={{ flex: 1 }} />
        {droits.ipm_gestion && <Bouton Icone={Plus} onClick={() => setAction('generer')}>Générer les cotisations</Bouton>}
        {droits.ipm_controle_paie && <Bouton Icone={Check} onClick={() => setAction('constater')}>Constater le prélèvement de la période</Bouton>}
      </div>
      <BandeauInfo ton="info">Aucun montant n'est fixé par le logiciel : la génération exige un mode de cotisation paramétré et sourcé (règlement intérieur). La contribution de SOGAS est une ligne distincte. Le prélèvement est constaté par la paie.</BandeauInfo>
      {message && <BandeauInfo ton={message.ton}>{message.texte}</BandeauInfo>}
      {!data ? <Squelette hauteur={60} /> : data.lignes.length === 0 ? <EtatVide titre="Aucune cotisation pour cette période" /> : (
        <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
          <table className="rh-tableau">
            <thead><tr><th>Salarié</th><th style={{ textAlign: 'right' }}>Cotisation salariale</th><th style={{ textAlign: 'right' }}>Contribution SOGAS</th><th>Mode</th><th>État</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {data.lignes.map((c) => (
                <tr key={c.id} className="rh-ligne-inactive">
                  <td>{nomSalarie(c)}<div className="rh-cellule-secondaire">{c.matricule}</div></td>
                  <td style={{ textAlign: 'right' }}>{fmtF(c.montant)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtF(c.montant_employeur)}</td>
                  <td>{c.mode_calcul === 'FORFAIT' ? 'Forfait' : c.mode_calcul === 'TAUX' ? 'Taux' : NON_RENSEIGNE}</td>
                  <td><Etat ton={c.statut === 'preleve' ? 'succes' : 'alerte'}>{c.statut === 'preleve' ? 'Prélèvement constaté' : 'En attente de la paie'}</Etat></td>
                  <td>{droits.ipm_controle_paie && c.statut === 'en_attente' && <Bouton petit onClick={async () => { try { await api.post(`/cotisations/${c.id}/prelevement`); charger(); } catch (e) { setMessage({ ton: 'danger', texte: messageErreur(e) }); } }}>Constater</Bouton>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={data.page} pages={Math.max(1, Math.ceil(data.total / data.limite))} total={data.total} taille={data.limite} onPage={setPage} />}
      {action === 'generer' && (
        <FenetreSaisie titre={`Générer les cotisations — ${periode}`} onFermer={() => setAction(null)} champs={[]}
          avertissement="Une ligne par salarié en CDI ou CDD au statut Actif, selon le mode de cotisation actif. Les lignes déjà générées ne sont pas recalculées."
          onFait={() => fait('Cotisations générées.')} valider={() => api.post('/cotisations/generer', { periode })} />
      )}
      {action === 'constater' && (
        <FenetreSaisie titre={`Constater le prélèvement — ${periode}`} onFermer={() => setAction(null)} champs={[]}
          avertissement="À faire uniquement après le traitement réel de la paie : chaque salarié concerné est informé que sa cotisation a été retenue sur son salaire."
          onFait={() => fait('Prélèvement constaté.')} valider={() => api.post(`/cotisations/periode/${periode}/prelevement`)} />
      )}
    </Carte>
  );
}

// ═══ 6. SITUATION FINANCIÈRE (sans donnée médicale) ═════════════════════════
function Situation({ api }) {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [s, setS] = useState(null);
  const [erreur, setErreur] = useState(null);
  useEffect(() => { setS(null); api.get('/situation-financiere', { annee }).then(setS).catch((e) => setErreur(messageErreur(e))); }, [api, annee]);
  const v = (x) => (x === null ? 'Masqué' : fmtF(x));
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Année
          <select className="rh-champ-saisie" style={{ width: 'auto' }} value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
            {[0, 1, 2].map((i) => { const a = new Date().getFullYear() - i; return <option key={a} value={a}>{a}</option>; })}
          </select>
        </label>
      </div>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!s ? <Squelette hauteur={140} /> : (
        <>
          {s.agregats_seuls && <BandeauInfo ton="info">Vue agrégée : toute valeur calculée sur moins de {s.seuil_anonymat.valeur} salariés est masquée{s.seuil_anonymat.statut === 'PROVISOIRE' ? ' (seuil provisoire, en attente de décision)' : ''}.</BandeauInfo>}
          <Carte titre="Totaux de l'année" Icone={BarChart3}>
            <Grille>
              <Champ libelle="Cotisations appelées" valeur={v(s.totaux.cotisations_appelees)} />
              <Champ libelle="Cotisations prélevées (constatées par la paie)" valeur={v(s.totaux.cotisations_prelevees)} />
              <Champ libelle="Contribution SOGAS" valeur={v(s.totaux.contributions_employeur)} />
              <Champ libelle="Dépenses prises en charge par l'IPM" valeur={v(s.totaux.depenses_prises_en_charge)} />
              <Champ libelle="Restes à charge nés" valeur={v(s.totaux.restes_a_charge_nes)} />
              <Champ libelle="Sommes récupérées" valeur={v(s.totaux.sommes_recuperees)} />
            </Grille>
          </Carte>
          <Carte titre="Créances sur les salariés" Icone={Wallet}>
            <Grille>
              <Champ libelle="Reste à recouvrer" valeur={v(s.creances_salaries.reste_a_recouvrer)} />
              <Champ libelle="Salariés débiteurs" valeur={s.creances_salaries.nb_debiteurs === null ? 'Masqué' : String(s.creances_salaries.nb_debiteurs)} />
              <Champ libelle="Retenues arrêtées en attente de la paie" valeur={v(s.creances_salaries.retenues_arretees_en_attente_de_la_paie)} />
              {s.creances_salaries.trop_percus !== null && <Champ libelle="Trop-perçus à restituer" valeur={fmtF(s.creances_salaries.trop_percus)} />}
            </Grille>
          </Carte>
          <Carte titre="Évolution mensuelle" Icone={History}>
            <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
              <table className="rh-tableau">
                <thead><tr><th>Mois</th><th style={{ textAlign: 'right' }}>Cotisations appelées</th><th style={{ textAlign: 'right' }}>Prélevées</th><th style={{ textAlign: 'right' }}>Contribution SOGAS</th><th style={{ textAlign: 'right' }}>Dépenses IPM</th><th style={{ textAlign: 'right' }}>Restes à charge</th><th style={{ textAlign: 'right' }}>Récupéré</th></tr></thead>
                <tbody>
                  {s.mois.map((m) => (
                    <tr key={m.periode} className="rh-ligne-inactive">
                      <td>{m.periode}</td>
                      {['cotisations_appelees', 'cotisations_prelevees', 'contributions_employeur', 'depenses_prises_en_charge', 'restes_a_charge_nes', 'sommes_recuperees'].map((k) => <td key={k} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{v(m[k])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
          <BandeauInfo ton="info" titre="Pas encore suivi dans ce socle">{s.non_suivi_dans_ce_socle.map((x) => <div key={x}>{x}</div>)}</BandeauInfo>
        </>
      )}
    </>
  );
}

// ═══ 7. PARAMÈTRES DATÉS ET SOURCÉS ═════════════════════════════════════════
const LIBELLE_STATUT_PARAM = { BROUILLON: 'Brouillon', A_VALIDER: 'À valider', ACTIF: 'Actif', ARCHIVE: 'Archivé' };
const TON_STATUT_PARAM = { BROUILLON: 'neutre', A_VALIDER: 'alerte', ACTIF: 'succes', ARCHIVE: 'neutre' };
function Parametres({ api, droits, moi }) {
  const [cat, setCat] = useState(null);
  const [versions, setVersions] = useState([]);
  const [soins, setSoins] = useState([]);
  const [action, setAction] = useState(null);
  const [erreur, setErreur] = useState(null);
  const charger = useCallback(() => {
    api.get('/parametres-catalogue').then(setCat).catch((e) => setErreur(messageErreur(e)));
    api.get('/parametres-versions').then(setVersions).catch(() => setVersions([]));
    if (droits.ipm_gestion || droits.ipm_donnees_medicales) api.get('/types-soins').then(setSoins).catch(() => setSoins([]));
  }, [api, droits]);
  useEffect(() => { charger(); }, [charger]);
  const agir = async (fn) => { setErreur(null); try { await fn(); charger(); } catch (e) { setErreur(messageErreur(e)); } };
  // Brouillon ou version à valider : son auteur ou la supervision. Version active : supervision,
  // ni par son auteur ni par son valideur.
  const peutArchiver = (v) => {
    if (!droits.ipm_gestion || v.statut === 'ARCHIVE') return false;
    if (v.statut === 'ACTIF') return !!droits.ipm_supervision && v.cree_par !== moi && v.valide_par !== moi;
    return v.cree_par === moi || !!droits.ipm_supervision;
  };
  if (!cat) return erreur ? <BandeauInfo ton="danger">{erreur}</BandeauInfo> : <Squelette hauteur={120} />;
  const domaines = [...new Set(cat.definitions.map((d) => d.domaine))];
  const unite = (u) => ({ POURCENT: '%', FCFA: 'FCFA', ANS: 'ans', MOIS: 'mois' }[u] || u);
  return (
    <>
      <BandeauInfo ton="info" titre="Aucune valeur n'est préremplie">Les valeurs réelles viennent du règlement intérieur et des documents officiels de l'IPM, avec leur référence. Une version est saisie en brouillon, soumise, puis validée par une autre personne habilitée. Aucune version ne s'applique à une période déjà arrêtée.</BandeauInfo>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {(droits.ipm_gestion || droits.ipm_donnees_medicales) && <TypesSoins api={api} droits={droits} />}
      {domaines.map((dom) => (
        <Carte key={dom} titre={dom} Icone={SlidersHorizontal}>
          {cat.definitions.filter((d) => d.domaine === dom).map((d) => {
            const vs = versions.filter((v) => v.code === d.code);
            return (
              <div key={d.code} style={{ borderBottom: '1px solid var(--sg-bord)', padding: '10px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ flex: 1, minWidth: 200 }}>{d.libelle} <span className="rh-cellule-secondaire">({unite(d.unite)})</span></strong>
                  {vs.every((v) => v.statut !== 'ACTIF') && <Etat ton="alerte">Non paramétré</Etat>}
                  {droits.ipm_gestion && <Bouton petit Icone={Plus} onClick={() => setAction({ type: 'nouvelle', d })}>Nouvelle version</Bouton>}
                </div>
                {d.aide && <div className="rh-cellule-secondaire">{d.aide}</div>}
                {vs.length > 0 && (
                  <ul className="rh-liste">
                    {vs.map((v) => (
                      <li key={v.id}>
                        <span style={{ flex: 1 }}>
                          {v.contexte ? <strong>{(soins.find((s) => s.code === v.contexte) || {}).libelle || v.contexte} · </strong> : null}
                          v{v.version} · {v.valeur} {unite(v.unite)} · du {formaterDate(v.date_debut)}{v.date_fin ? ` au ${formaterDate(v.date_fin)}` : ''}
                          <div className="rh-cellule-secondaire">Source : {v.source_reference} · saisi par {v.cree_par_nom}{v.valide_par_nom ? ` · validé par ${v.valide_par_nom}` : ''}</div>
                        </span>
                        <Etat ton={TON_STATUT_PARAM[v.statut]}>{LIBELLE_STATUT_PARAM[v.statut]}</Etat>
                        {droits.ipm_gestion && v.statut === 'BROUILLON' && v.cree_par === moi && <Bouton petit Icone={Send} onClick={() => agir(() => api.post(`/parametres-versions/${v.id}/soumettre`))}>Soumettre</Bouton>}
                        {v.statut === 'A_VALIDER' && (droits.ipm_supervision && v.cree_par !== moi
                          ? <Bouton petit variante="primaire" Icone={Check} onClick={() => agir(() => api.post(`/parametres-versions/${v.id}/valider`))}>Valider</Bouton>
                          : <span className="rh-cellule-secondaire">{v.cree_par === moi ? 'En attente d\'un Gestionnaire IPM (vous en êtes l\'auteur)' : 'En attente du Gestionnaire IPM'}</span>)}
                        {peutArchiver(v) && <Bouton petit variante="discret" Icone={Archive} onClick={() => setAction({ type: 'archiver', v })}>Archiver</Bouton>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </Carte>
      ))}
      {action?.type === 'nouvelle' && (
        <FenetreSaisie titre={`Nouvelle version — ${action.d.libelle}`} onFermer={() => setAction(null)} onFait={() => { setAction(null); charger(); }}
          initial={{ source_type: 'REGLEMENT_INTERIEUR', contexte: '' }}
          champs={[
            ...(action.d.contexte ? [{ cle: 'contexte', libelle: 'Type de soin', requis: true, options: [{ valeur: '', libelle: 'Choisir' }, ...soins.map((s) => ({ valeur: s.code, libelle: s.libelle }))] }] : []),
            { cle: 'valeur', libelle: `Valeur (${unite(action.d.unite)})`, requis: true, type: 'number', min: action.d.min },
            { cle: 'date_debut', libelle: 'Date de début', requis: true, type: 'date' },
            { cle: 'date_fin', libelle: 'Date de fin (facultative)', type: 'date' },
            { cle: 'source_type', libelle: 'Source', requis: true, options: cat.sources.map((s) => ({ valeur: s.code, libelle: s.libelle })) },
            { cle: 'source_reference', libelle: 'Référence du document', requis: true, aide: 'Article du règlement intérieur, numéro du procès-verbal, texte officiel…' },
            { cle: 'motif', libelle: 'Commentaire', texteLong: true },
          ]}
          valider={(v) => api.post('/parametres-versions', { ...v, code: action.d.code, valeur: Number(v.valeur), date_fin: v.date_fin || null })} />
      )}
      {action?.type === 'archiver' && (
        <FenetreSaisie titre="Archiver la version" onFermer={() => setAction(null)} onFait={() => { setAction(null); charger(); }}
          champs={[{ cle: 'motif', libelle: 'Motif', requis: true, texteLong: true }]}
          valider={(v) => api.post(`/parametres-versions/${action.v.id}/archiver`, v)} />
      )}
    </>
  );
}

function TypesSoins({ api, droits }) {
  const [liste, setListe] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [erreur, setErreur] = useState(null);
  const charger = useCallback(() => { api.get('/types-soins').then(setListe).catch(() => setListe([])); }, [api]);
  useEffect(() => { charger(); }, [charger]);
  return (
    <Carte titre="Types de soins" Icone={Stethoscope} action={droits.ipm_supervision && <Bouton petit Icone={Plus} onClick={() => setAjout(true)}>Ajouter</Bouton>}>
      <p className="rh-cellule-secondaire">Référentiel des rubriques de soins. Le taux de chaque type est un paramètre daté (« Taux de prise en charge »), jamais une valeur du référentiel.</p>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!liste ? <Squelette hauteur={40} /> : liste.length === 0 ? <EtatVide titre="Aucun type de soin" /> : (
        <ul className="rh-liste">
          {liste.map((t) => (
            <li key={t.id}>
              <span style={{ flex: 1 }}>{t.libelle} <span className="rh-cellule-secondaire">· {t.code} · reconnu : {t.taux_actif === null ? 'non paramétré' : `${t.taux_actif} %`} · non reconnu : {t.taux_non_reconnu_actif === null ? 'non paramétré' : `${t.taux_non_reconnu_actif} %`}</span></span>
              <Etat ton={t.actif ? 'succes' : 'neutre'}>{t.actif ? 'Actif' : 'Inactif'}</Etat>
              {droits.ipm_supervision && <Bouton petit variante="discret" onClick={async () => { try { await api.patch(`/types-soins/${t.id}`, { actif: !t.actif }); charger(); } catch (e) { setErreur(messageErreur(e)); } }}>{t.actif ? 'Désactiver' : 'Activer'}</Bouton>}
            </li>
          ))}
        </ul>
      )}
      {ajout && (
        <FenetreSaisie titre="Ajouter un type de soin" onFermer={() => setAjout(false)} onFait={() => { setAjout(false); charger(); }}
          champs={[{ cle: 'libelle', libelle: 'Libellé', requis: true }]} valider={(v) => api.post('/types-soins', v)} />
      )}
    </Carte>
  );
}

// ═══ 8. ACCÈS EXCEPTIONNELS ═════════════════════════════════════════════════
function AccesExceptionnels({ api, droits, gardeFous }) {
  const dureeMax = (gardeFous || []).find((g) => g.code === 'ACCES_EXCEPTIONNEL_DUREE_MAX_HEURES') || null; // source unique : le serveur
  const [liste, setListe] = useState(null);
  const [beneficiaire, setBeneficiaire] = useState(null);
  const [f, setF] = useState({ motif: '', duree_heures: 2 });
  const [erreur, setErreur] = useState(null);
  const charger = useCallback(() => { api.get('/acces-exceptionnels').then(setListe).catch((e) => setErreur(messageErreur(e))); }, [api]);
  useEffect(() => { charger(); }, [charger]);
  const accorder = async () => {
    setErreur(null);
    try { await api.post('/acces-exceptionnels', { beneficiaire_id: beneficiaire.id, ...f, duree_heures: Number(f.duree_heures) }); setBeneficiaire(null); setF({ motif: '', duree_heures: 2 }); charger(); } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <>
      <BandeauInfo ton="alerte" titre="Usage exceptionnel">Pour une intervention technique qui exige de voir le domaine médical : lecture seule, {dureeMax ? `${dureeMax.valeur} heures au plus${dureeMax.statut === 'PROVISOIRE' ? ' (durée maximale provisoire, en attente de décision)' : ''}` : 'durée limitée'}, motif obligatoire, accordé par un Gestionnaire IPM ayant l'accès aux données médicales à une autre personne. Chaque consultation est tracée.</BandeauInfo>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!(droits.ipm_supervision && droits.ipm_donnees_medicales) ? (
        <BandeauInfo ton="info">Accorder un accès exceptionnel demande la supervision IPM et l'accès aux données médicales. Vous pouvez consulter et révoquer les accès en cours.</BandeauInfo>
      ) : (
      <Carte titre="Accorder un accès" Icone={KeyRound}>
        <ChampForm libelle="Bénéficiaire" requis><RechercheSalarie api={api} valeur={beneficiaire} onChoix={setBeneficiaire} /></ChampForm>
        <ChampForm libelle={dureeMax ? `Durée (heures, ${dureeMax.valeur} au plus)` : 'Durée (heures)'} requis id="a-duree"><input id="a-duree" className="rh-champ-saisie" type="number" min="1" max={dureeMax ? dureeMax.valeur : undefined} value={f.duree_heures} onChange={(e) => setF({ ...f, duree_heures: e.target.value })} /></ChampForm>
        <ChampForm libelle="Motif" requis aide="Nature de l'intervention, ticket, demandeur." id="a-motif"><textarea id="a-motif" className="rh-champ-saisie" rows={2} value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} /></ChampForm>
        <PiedFenetre><Bouton variante="primaire" Icone={KeyRound} disabled={!dureeMax || !beneficiaire || f.motif.trim().length < 10} onClick={accorder}>Accorder</Bouton></PiedFenetre>
      </Carte>
      )}
      <Carte titre="Accès accordés" Icone={History}>
        {!liste ? <Squelette hauteur={50} /> : liste.length === 0 ? <EtatVide titre="Aucun accès exceptionnel" /> : (
          <ul className="rh-liste">
            {liste.map((a) => (
              <li key={a.id}>
                <span style={{ flex: 1 }}>{a.beneficiaire_nom} · jusqu'au {new Date(a.fin).toLocaleString('fr-FR')}<div className="rh-cellule-secondaire">{a.motif} — accordé par {a.accorde_par_nom}{a.revoque_le ? ` · révoqué par ${a.revoque_par_nom}` : ''}</div></span>
                <Etat ton={a.actif ? 'alerte' : 'neutre'}>{a.actif ? 'En cours' : a.revoque_le ? 'Révoqué' : 'Expiré'}</Etat>
                {a.actif && <Bouton petit variante="danger" Icone={Ban} onClick={async () => { try { await api.post(`/acces-exceptionnels/${a.id}/revoquer`); charger(); } catch (e) { setErreur(messageErreur(e)); } }}>Révoquer</Bouton>}
              </li>
            ))}
          </ul>
        )}
      </Carte>
    </>
  );
}

// ═══ 9. JOURNAL DE CONTRÔLE (supervision) ═══════════════════════════════════
const LIBELLES_ACTION = {
  IPM_FACTURE: 'Prise en charge enregistrée',
  IPM_FACTURE_ANNULEE: 'Prise en charge annulée',
  IPM_MVT_PROPOSE: 'Correction proposée',
  IPM_MVT_VALIDE: 'Correction validée',
  IPM_MVT_REJETE: 'Correction rejetée',
  IPM_RETENUE_PROPOSEE: 'Retenue proposée',
  IPM_RETENUE_MODIFIEE: 'Retenue modifiée',
  IPM_RETENUE_ANNULEE: 'Retenue annulée',
  IPM_RETOUR_PAIE: 'Retour de la paie enregistré',
  IPM_ARRET_DEMANDE: 'Arrêt de période demandé',
  IPM_PERIODE_ARRETEE: 'Arrêt de période confirmé',
  IPM_ARRET_ANNULE: 'Demande d\'arrêt annulée',
  IPM_PARAM_BROUILLON: 'Paramètre saisi (brouillon)',
  IPM_PARAM_MODIFIE: 'Paramètre modifié',
  IPM_PARAM_SOUMIS: 'Paramètre soumis à validation',
  IPM_PARAM_ACTIVE: 'Paramètre validé',
  IPM_PARAM_REMPLACE: 'Paramètre remplacé',
  IPM_PARAM_ARCHIVE: 'Paramètre archivé',
  IPM_PRESTA_CREE: 'Prestataire de santé ajouté',
  IPM_PRESTA_MODIFIE: 'Prestataire de santé modifié',
  IPM_PRESTA_RECONNU: 'Prestataire reconnu par l\'IPM',
  IPM_PRESTA_NON_RECON: 'Reconnaissance du prestataire retirée',
  IPM_TYPE_SOIN_CREE: 'Type de soin ajouté',
  IPM_TYPE_SOIN_MODIF: 'Type de soin modifié',
  IPM_ACCES_ACCORDE: 'Accès exceptionnel accordé',
  IPM_ACCES_REVOQUE: 'Accès exceptionnel révoqué',
  IPM_DOC_ARRETE: 'Relevé arrêté',
  IPM_DOC_REUTILISE: 'Relevé arrêté réutilisé',
  IPM_DOC_REMIS: 'Relevé remis',
  IPM_DOC_TELECHARGE: 'Document téléchargé',
  IPM_DOC_PROVISOIRE: 'Relevé provisoire consulté',
  IPM_COTIS_GENEREES: 'Cotisations générées',
  IPM_COTIS_PRELEVEES: 'Prélèvement des cotisations constaté',
  IPM_CONSULT_MEDICAL: 'Consultation de données médicales',
  IPM_CONSULT_FINANCE: 'Consultation de données financières',
  IPM_REFUS: 'Prise en charge refusée',
};
const libelleAction = (a) => LIBELLES_ACTION[a] || (String(a || '').startsWith('IPM_CONSULT_') ? 'Consultation' : 'Opération IPM');

function JournalControle({ api }) {
  const [page, setPage] = useState(1);
  const [consultations, setConsultations] = useState(false);
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    setErreur(null);
    api.get('/journal', { page, limit: 25, consultations: consultations ? 'oui' : undefined })
      .then(setData).catch((e) => setErreur(messageErreur(e)));
  }, [api, page, consultations]);
  return (
    <Carte titre="Journal de contrôle" Icone={ScrollText}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={consultations} onChange={(e) => { setConsultations(e.target.checked); setPage(1); }} /> Inclure les consultations
        </label>
        <span className="rh-cellule-secondaire">Qui a fait quoi, avec quelle fonction, sur quel dossier. Aucune donnée médicale.</span>
      </div>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!data && !erreur && <Squelette hauteur={120} />}
      {data && data.lignes.length === 0 && <EtatVide titre="Aucune opération" />}
      {data && data.lignes.length > 0 && (
        <ul className="rh-liste">
          {data.lignes.map((l) => (
            <li key={l.id} style={{ alignItems: 'flex-start' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{libelleAction(l.action)}</strong>
                {l.dossier ? <span> · {l.dossier}{l.dossier_matricule ? <span className="rh-cellule-secondaire"> ({l.dossier_matricule})</span> : null}</span> : null}
                <div className="rh-cellule-secondaire">
                  {new Date(l.created_at).toLocaleString('fr-FR')} · {l.acteur || 'Traitement automatique'}
                  {l.type_acteur && l.type_acteur !== 'Système' ? ` · ${l.type_acteur}` : ''}
                  {l.identifiant_acteur ? ` · ${l.identifiant_acteur}` : ''}
                  {l.fonction ? ` · ${l.fonction}` : ''}
                </div>
              </span>
            </li>
          ))}
        </ul>
      )}
      {data && <Pagination page={data.page} pages={Math.max(1, Math.ceil(data.total / data.limite))} total={data.total} taille={data.limite} onPage={setPage} />}
    </Carte>
  );
}

// ═══ PAGE ═══════════════════════════════════════════════════════════════════
const ONGLETS = [
  { cle: 'controle', libelle: 'Dettes et retenues', Icone: Wallet, visible: (d) => d.ipm_gestion || d.ipm_controle_rh || d.ipm_controle_paie || d.ipm_controle_financier },
  { cle: 'a-valider', libelle: 'À valider', Icone: CheckCheck, visible: (d) => d.ipm_gestion },
  { cle: 'prestations', libelle: 'Prestations', Icone: Stethoscope, visible: (d, x) => d.ipm_donnees_medicales || x },
  { cle: 'prestataires', libelle: 'Prestataires', Icone: Building2, visible: (d) => d.ipm_gestion || d.ipm_donnees_medicales },
  { cle: 'cotisations', libelle: 'Cotisations', Icone: Receipt, visible: (d) => d.ipm_gestion || d.ipm_controle_paie || d.ipm_controle_financier },
  { cle: 'situation', libelle: 'Situation financière', Icone: BarChart3, visible: (d) => d.ipm_gestion || d.ipm_controle_financier || d.ipm_commission },
  { cle: 'parametres', libelle: 'Paramètres', Icone: SlidersHorizontal, visible: (d) => Object.values(d).some(Boolean) },
  { cle: 'acces', libelle: 'Accès exceptionnels', Icone: KeyRound, visible: (d) => d.ipm_supervision },
  { cle: 'journal', libelle: 'Journal de contrôle', Icone: ScrollText, visible: (d) => d.ipm_supervision },
];

export default function IpmPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { employe } = useAuth();
  const [params, setParams] = useSearchParams();
  const [droits, setDroits] = useState(null);
  const [moiServeur, setMoiServeur] = useState(null);
  const [fonction, setFonction] = useState(null);
  const [gardeFous, setGardeFous] = useState([]);
  const [exceptionnel, setExceptionnel] = useState(false);
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    api.get('/mes-droits').then((r) => {
      setDroits(r.droits); setGardeFous(r.garde_fous || []); setExceptionnel(!!r.acces_exceptionnel);
      setMoiServeur(r.moi || null); setFonction(r.fonction_ipm || null);
    }).catch((e) => setErreur(messageErreur(e)));
  }, [api]);
  const visibles = droits ? ONGLETS.filter((o) => o.visible(droits, exceptionnel)) : [];
  const actif = visibles.find((o) => o.cle === params.get('onglet'))?.cle || visibles[0]?.cle;
  const changer = (cle) => setParams({ onglet: cle }, { replace: true });
  const moi = moiServeur || employe?.id;
  const externe = employe?.nature === 'EXTERNE';
  return (
    <Layout>
      <div className="rh-page rh-page-large">
        <EnTetePage fil={[{ libelle: 'IPM' }]} titre="IPM — Institution de prévoyance maladie"
          sousTitre="Dette du salarié = somme de ses restes à charge. Accès sur fonction ou habilitation explicite ; le domaine financier ne montre aucune donnée médicale."
          actions={(fonction || droits?.ipm_supervision) && (
            <>
              {fonction && (
                <Badge ton="info" title={fonction.date_fin ? `Jusqu'au ${formaterDate(fonction.date_fin)}` : undefined}>
                  {fonction.libelle}{externe ? ' · prestataire externe' : ''}{fonction.acces_medical ? '' : ' · sans données médicales'}
                </Badge>
              )}
              {droits?.ipm_supervision && <Link to="/ipm/equipe" className="rh-btn"><Users size={16} aria-hidden="true" />Équipe IPM</Link>}
            </>
          )} />
        {droits && (droits.ipm_gestion || droits.ipm_controle_rh || droits.ipm_controle_paie || droits.ipm_controle_financier) && (
          <div style={{ maxWidth: 520, marginBottom: 12 }}>
            <RechercheSalarie api={api} valeur={null} onChoix={(s) => s && navigate(`/ipm/comptes/${s.id}`)} libelleChoix="Ouvrir le compte" libelle="Rechercher un participant" />
          </div>
        )}
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {!droits && !erreur && <Squelette hauteur={200} />}
        {droits && visibles.length === 0 && (
          <EtatVide Icone={ShieldAlert} titre="Aucune habilitation IPM" texte="Les fonctions IPM s'ouvrent uniquement sur fonction Agent ou Gestionnaire IPM attribuée nominativement, ou sur habilitation de contrôle attribuée à votre poste." />
        )}
        {droits && visibles.length > 0 && (
          <>
            <Onglets onglets={visibles} actif={actif} onChange={changer} libelle="Sections IPM" />
            <div role="tabpanel" id={`panneau-${actif}`} aria-labelledby={`onglet-${actif}`} style={{ marginTop: 16 }}>
              {actif === 'controle' && <ControleRetenues api={api} droits={droits} moi={moi} />}
              {actif === 'a-valider' && <AValider api={api} droits={droits} moi={moi} />}
              {actif === 'prestations' && <Prestations api={api} droits={droits} moi={moi} exceptionnel={exceptionnel && !droits.ipm_donnees_medicales} />}
              {actif === 'prestataires' && <Prestataires api={api} droits={droits} />}
              {actif === 'cotisations' && <Cotisations api={api} droits={droits} />}
              {actif === 'situation' && <Situation api={api} />}
              {actif === 'parametres' && <Parametres api={api} droits={droits} moi={moi} />}
              {actif === 'acces' && <AccesExceptionnels api={api} droits={droits} gardeFous={gardeFous} />}
              {actif === 'journal' && <JournalControle api={api} />}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
